import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, MailWarning, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/pages/CompanyFormDialog";
import { Spinner } from "@/components/common/Common";
import { api, formatError } from "@/lib/api";

const split = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

// Manual send only: compose -> preview (shows safe-mode routing) -> send.
export function ComposeEmailDialog({ draft, onClose, contractId, personId, onSent }) {
  const [form, setForm] = useState({ to: "", cc: "", subject: "", body: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm({ to: (draft?.to || []).join(", "), cc: (draft?.cc || []).join(", "), subject: draft?.subject || "", body: draft?.body || "" });
  }, [draft]);

  const payload = () => ({ to: split(form.to), cc: split(form.cc), subject: form.subject, body: form.body, contract_id: contractId, person_id: personId });
  const doPreview = async () => {
    setBusy(true);
    try { setPreview((await api.post("/emails/preview", payload())).data); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/emails/send", payload());
      if (data.status === "sent") toast.success(`Email sent to ${data.actual_to.join(", ")}${data.safe_mode ? " (safe mode)" : ""}`);
      else toast.error(`Email ${data.status}: ${data.error || ""}`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="compose-email-dialog">
        <DialogHeader><DialogTitle>{preview ? "Preview email" : "Compose email"}</DialogTitle></DialogHeader>
        {!preview ? (
          <div className="space-y-3">
            <Field label="To (comma separated)"><Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="compose-to-input" /></Field>
            <Field label="CC"><Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} data-testid="compose-cc-input" /></Field>
            <Field label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="compose-subject-input" /></Field>
            <Field label="Message"><Textarea rows={9} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="compose-body-input" /></Field>
          </div>
        ) : (
          <div className="space-y-3 text-sm" data-testid="compose-preview">
            {preview.safe_mode && (
              <div className="flex gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2" data-testid="compose-safe-mode-note">
                <MailWarning className="h-4 w-4 mt-0.5 shrink-0" />
                <span>SAFE MODE — this will go to <strong>{preview.actual.to.join(", ")}</strong> only. Originally to: {preview.original.to.join(", ") || "—"}{preview.original.cc.length ? `; CC ${preview.original.cc.join(", ")}` : ""}</span>
              </div>
            )}
            {preview.blocked.length > 0 && (
              <div className="flex gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2" data-testid="compose-blocked-note">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Blocked recipient(s): {preview.blocked.join(", ")}. This email will NOT be sent. Remove them to continue.</span>
              </div>
            )}
            <div><span className="text-slate-400">Subject: </span><strong data-testid="compose-preview-subject">{preview.subject}</strong></div>
            <iframe title="Email preview" sandbox="" srcDoc={preview.html} className="w-full h-64 border border-slate-200 rounded-md bg-white" />
          </div>
        )}
        <DialogFooter className="gap-2">
          {preview ? (
            <>
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="compose-back-button">Back to edit</Button>
              <Button onClick={send} disabled={busy || !preview.will_send} data-testid="compose-send-button">{busy ? <Spinner className="mr-2" /> : <Send className="h-4 w-4 mr-1" />}Send now</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={doPreview} disabled={busy || !form.to.trim() || !form.subject.trim() || !form.body.trim()} data-testid="compose-preview-button">Preview</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
